import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, faArrowRight, faSave, faCheck, faCheckSquare, faSquare,
  faHandshake, faFileContract, faTruck, faSearch, faUserShield,
  faUser, faBuilding, faUsers, faMoneyBillWave, faTimes, faPlus,
  faUserPlus, faTag, faCalculator, faTint
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../utils/axiosConfig';
import Layout from './Layout';
import logo from '../assets/images/IMT_logo.png';
import Footer from './ui/Footer';
import { useFlashMessage } from './FlashMessage';
import { invalidateGroupingsCache } from './OperationsAccessList';
import BackToTopButton from './BackToTopButton';
import DeliveryBackModal from './DeliveryBackModal';
import RecordLoadProblem, { describeLoadFailure } from './ui/RecordLoadProblem';
import { goToTradeOps, useTradeOpsDesk } from '../utils/tradeOpsDesk';
import './OperationsAccessForm.css';
import '../components/ui/HMSHeaderStyles.css';

const OperationsAccessForm = () => {
  const history = useHistory();
  const location = useLocation();
  const { id: editId } = useParams();
  const isEditing = Boolean(editId);
  const { showSuccess, showError } = useFlashMessage();

  // Derive commodity flavour from the URL path:
  // /dashboard/trades/solids/operations-access/...  -> 'solids'
  // /dashboard/trades/fuels/operations-access/...   -> 'fuels'
  // /dashboard/trades/operations-access/...         -> legacy, treat as 'solids'
  const pathMatch = location.pathname.match(/\/dashboard\/trades\/(solids|fuels)\/operations-access/);
  // Row 127: an existing grouping's own commodity_type beats the address, so a
  // fuels grouping opened at the legacy address is no longer shown and saved as
  // Cargo (which dropped its fuel enquiries).
  const [groupingCommodity, setGroupingCommodity] = useState(null);
  const tradeOpsDesk = useTradeOpsDesk({
    recordCommodity: groupingCommodity,
    hintCommodity: pathMatch ? pathMatch[1] : null,
  });
  const commodityType = tradeOpsDesk.commodity;

  // Step navigation
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);

  // Step 1: Entities
  const [availableEntities, setAvailableEntities] = useState({ deals: [], trade_recaps: [], freight_recaps: [], deliveries: [], budgets: [], fuel_enquiries: [] });
  const [selectedDealIds, setSelectedDealIds] = useState([]);
  const [selectedTradeRecapIds, setSelectedTradeRecapIds] = useState([]);
  const [selectedFreightRecapIds, setSelectedFreightRecapIds] = useState([]);
  const [selectedDeliveryIds, setSelectedDeliveryIds] = useState([]);
  const [selectedBudgetIds, setSelectedBudgetIds] = useState([]);
  // Fuel enquiries are Fuels-only and manual-only (no auto-linking) — they have
  // no place in the resolve-linked-entities cascade, so a plain selected-id set.
  const [selectedFuelEnquiryIds, setSelectedFuelEnquiryIds] = useState([]);
  const [autoIncludedRaw, setAutoIncludedRaw] = useState({ deal_ids: [], trade_recap_ids: [], freight_recap_ids: [], delivery_ids: [], budget_ids: [] });
  const [excludedAutoIds, setExcludedAutoIds] = useState({ deal_ids: [], trade_recap_ids: [], freight_recap_ids: [], delivery_ids: [], budget_ids: [] });
  const [entitySearch, setEntitySearch] = useState('');
  const [loadingEntities, setLoadingEntities] = useState(false);

  // Step 2: Main Operator
  const [operators, setOperators] = useState([]);
  const [selectedOperatorId, setSelectedOperatorId] = useState(null);
  const [operatorFinancialAccess, setOperatorFinancialAccess] = useState(false);
  const [operatorSearch, setOperatorSearch] = useState('');
  // Three states, not two. "Still loading", "the server refused/failed" and
  // "the server answered, and the answer is nobody" all used to end up as an
  // empty array drawn as an empty box, so a broken request was indistinguishable
  // from an empty desk. Each now says its own thing on screen.
  const [loadingOperators, setLoadingOperators] = useState(true);
  const [operatorsError, setOperatorsError] = useState(null);
  // The server's own sentence about why this list is wider than intended —
  // e.g. nobody has marked a department as the Fuels operations desk yet, so
  // it is showing every Bunkers office person instead. Written once, on the
  // server, so the screen cannot drift from the rule it is describing.
  const [operatorsNotice, setOperatorsNotice] = useState('');
  // Who is in the list only because this grouping already names them. The
  // server decides this, because the server is what forced them in.
  const [incumbentOperatorIds, setIncumbentOperatorIds] = useState([]);
  // The operator this grouping ALREADY has, straight from the grouping itself.
  // Kept separately from the candidate list on purpose: an operator whose job
  // title has been retired, or who has moved off the desk, is no longer a
  // candidate but is still this grouping's operator and must still be drawn.
  const [existingOperator, setExistingOperator] = useState(null);

  // Step 3: Office Users
  const [officeUsers, setOfficeUsers] = useState([]);
  const [officeUserSettings, setOfficeUserSettings] = useState({});
  const [officeUsersError, setOfficeUsersError] = useState(null);

  // Step 4: Additional Users
  const [allUsers, setAllUsers] = useState([]);
  const [allUsersError, setAllUsersError] = useState(null);
  const [additionalUsers, setAdditionalUsers] = useState([]);
  const [additionalUserSettings, setAdditionalUserSettings] = useState({});
  const [userSearch, setUserSearch] = useState('');

  // Grouping name
  const [groupingName, setGroupingName] = useState('');

  // Row 125: why the grouping being edited didn't load. While set, the screen
  // shows RecordLoadProblem INSTEAD of the wizard. A failed load used to leave
  // the blank wizard on screen, and its Save cleared the grouping's operator
  // and members.
  const [groupingLoadProblem, setGroupingLoadProblem] = useState(null);
  const [groupingLoadAttempt, setGroupingLoadAttempt] = useState(0);
  // Which grouping in the address has actually loaded; updating any other is refused.
  const [loadedGroupingId, setLoadedGroupingId] = useState(null);

  // Derived auto-included: raw auto-included minus user-excluded items
  const autoIncluded = {
    deal_ids: (autoIncludedRaw.deal_ids || []).filter(id => !excludedAutoIds.deal_ids.includes(id)),
    trade_recap_ids: (autoIncludedRaw.trade_recap_ids || []).filter(id => !excludedAutoIds.trade_recap_ids.includes(id)),
    freight_recap_ids: (autoIncludedRaw.freight_recap_ids || []).filter(id => !excludedAutoIds.freight_recap_ids.includes(id)),
    delivery_ids: (autoIncludedRaw.delivery_ids || []).filter(id => !excludedAutoIds.delivery_ids.includes(id)),
    budget_ids: (autoIncludedRaw.budget_ids || []).filter(id => !excludedAutoIds.budget_ids.includes(id)),
  };

  // The operator to DRAW, as opposed to the operators available to CHOOSE.
  //
  // AC6: an existing grouping must behave exactly as before. Its operator may
  // since have had their job title retired or been moved off the operations
  // desk, which takes them out of the candidate list — but not off this
  // grouping. Looking them up only in the candidate list is what made the
  // operator's name, office and job title render as nothing.
  const selectedOperatorDetails =
    operators.find(o => o.id === selectedOperatorId)
    || (existingOperator && existingOperator.id === selectedOperatorId ? existingOperator : null);

  // The rows the picker shows: the candidates, plus the operator this grouping
  // already has if the narrowing left them out. Keeping them in the list is
  // also what makes them re-selectable after a "Clear main operator" mistake.
  const operatorsToShow =
    existingOperator && !operators.some(o => o.id === existingOperator.id)
      ? [...operators, existingOperator]
      : operators;

  // True when the person on this grouping is only in the list because the
  // grouping names them — they would not qualify to be picked today. Worth
  // saying out loud: it is usually the reason an admin opened this screen.
  const selectedOperatorIsNoLongerACandidate = Boolean(
    selectedOperatorId
    && selectedOperatorDetails
    && (incumbentOperatorIds.includes(selectedOperatorId)
        || !operators.some(o => o.id === selectedOperatorId))
  );

  // Unsaved changes tracking
  const userHasInteracted = useRef(false);
  const isInitialLoad = useRef(true);
  const [showBackConfirmation, setShowBackConfirmation] = useState(false);

  // Query-param pre-selection from notification deep links
  const preSelectApplied = useRef(false);
  const queryParams = new URLSearchParams(location.search);
  const preSelectEntityType = queryParams.get('entity_type');
  const preSelectEntityId = queryParams.get('entity_id');

  // Load available entities on mount (and again if the loaded grouping turns
  // out to belong to the other desk than its address said -- row 127)
  useEffect(() => {
    fetchAvailableEntities(isEditing ? editId : null);
    fetchOperators();
    if (!isEditing) {
      isInitialLoad.current = false;
    }
  }, [commodityType]);

  // Auto-select entity from notification query params once entities are loaded
  useEffect(() => {
    if (isEditing || preSelectApplied.current || !preSelectEntityType || !preSelectEntityId) return;
    const entityId = parseInt(preSelectEntityId, 10);
    if (isNaN(entityId)) return;

    const typeMap = {
      deal: { list: availableEntities.deals, idField: 'offer_id', setter: setSelectedDealIds },
      recap: { list: [...(availableEntities.trade_recaps || []), ...(availableEntities.freight_recaps || [])], idField: 'id', setter: null },
      delivery: { list: availableEntities.deliveries, idField: 'id', setter: setSelectedDeliveryIds },
    };

    const config = typeMap[preSelectEntityType];
    if (!config) return;

    if (preSelectEntityType === 'recap') {
      const tradeMatch = (availableEntities.trade_recaps || []).find(e => e.id === entityId);
      const freightMatch = (availableEntities.freight_recaps || []).find(e => e.id === entityId);
      if (tradeMatch) {
        setSelectedTradeRecapIds([entityId]);
        preSelectApplied.current = true;
      } else if (freightMatch) {
        setSelectedFreightRecapIds([entityId]);
        preSelectApplied.current = true;
      }
    } else if (config.list && config.list.length > 0) {
      const match = config.list.find(e => e[config.idField] === entityId);
      if (match) {
        config.setter([entityId]);
        preSelectApplied.current = true;
      }
    }
  }, [availableEntities, isEditing, preSelectEntityType, preSelectEntityId]);

  // When editing, load existing grouping
  useEffect(() => {
    setGroupingLoadProblem(null);
    if (isEditing && editId) {
      fetchExistingGrouping();
    }
  }, [editId, groupingLoadAttempt]);

  const fetchAvailableEntities = async (excludeGroupingId = null) => {
    try {
      setLoadingEntities(true);
      const params = { commodity_type: commodityType };
      if (excludeGroupingId) {
        params.exclude_grouping_id = excludeGroupingId;
      }
      const response = await axiosInstance.get('/api/operations-access/available-entities/', { params });
      setAvailableEntities(response.data);
    } catch (error) {
      console.error('Error fetching available entities:', error);
      showError('Failed to load available entities.');
    } finally {
      setLoadingEntities(false);
    }
  };

  const fetchOperators = async () => {
    setLoadingOperators(true);
    setOperatorsError(null);
    setOperatorsNotice('');
    try {
      // Bunkers offers the fuels operations desk; Solids offers the operator
      // job titles. The backend branches on this param.
      //
      // `grouping_id` when editing: it makes the server include the operator
      // this grouping ALREADY has, even if that person no longer qualifies.
      // Without it, an admin retiring one job title took the operator straight
      // off this screen and the next save wrote the blank through.
      const params = { commodity_type: commodityType };
      if (isEditing && editId) {
        params.grouping_id = editId;
      }
      const response = await axiosInstance.get('/api/operations-access/operators/', { params });
      const list = response.data?.operators;
      if (!Array.isArray(list)) {
        // A shape we do not recognise is a failure, not an empty desk. Saying
        // "no operators" here would be inventing an answer out of a bad reply.
        throw new Error('Unexpected response from the operators list.');
      }
      setOperators(list);
      setOperatorsNotice(response.data?.notice || '');
      setIncumbentOperatorIds(
        Array.isArray(response.data?.incumbent_ids) ? response.data.incumbent_ids : []
      );
    } catch (error) {
      console.error('Error fetching operators:', error);
      // Do NOT leave the list quietly empty. A failed request used to be
      // swallowed here, and an empty operator list reads to the admin as "there
      // are no operators" — a claim about the world made by a broken request.
      setOperators([]);
      setIncumbentOperatorIds([]);
      setOperatorsError(
        error.response?.data?.detail
        || error.response?.data?.error
        || 'The list of operators could not be loaded. Refresh the page to try again.'
      );
    } finally {
      setLoadingOperators(false);
    }
  };

  const fetchExistingGrouping = async () => {
    try {
      const response = await axiosInstance.get(`/api/operations-access/groupings/${editId}/`);
      const data = response.data;
      setGroupingCommodity(data.commodity_type || null);
      setGroupingName(data.name);
      setSelectedOperatorId(data.main_operator);
      // Keep the whole operator record, not just the id. This is what the
      // screen draws when the person is no longer in the narrowed candidate
      // list — and what it falls back to if the candidate request itself fails.
      setExistingOperator(data.main_operator_details || null);
      setOperatorFinancialAccess(data.main_operator_has_financial_access);
      setSelectedDealIds(data.deal_list?.map(d => d.offer_id) || []);
      setSelectedTradeRecapIds(data.trade_recap_list?.map(r => r.id) || []);
      setSelectedFreightRecapIds(data.freight_recap_list?.map(f => f.id) || []);
      setSelectedDeliveryIds(data.delivery_list?.map(d => d.id) || []);
      setSelectedBudgetIds(data.budget_list?.map(b => b.id) || []);
      setSelectedFuelEnquiryIds(data.fuel_enquiry_list?.map(f => f.id) || []);
      
      // Reconstruct member settings
      const members = data.members || [];
      const offSettings = {};
      const addSettings = {};
      const addUsersList = [];
      members.forEach(m => {
        if (m.membership_type === 'OFFICE') {
          offSettings[m.employee] = { has_access: m.has_access, has_financial_access: m.has_financial_access };
        } else if (m.membership_type === 'MANUAL') {
          addSettings[m.employee] = { has_financial_access: m.has_financial_access };
          addUsersList.push(m.employee_details);
        }
      });
      setOfficeUserSettings(offSettings);
      setAdditionalUserSettings(addSettings);
      setAdditionalUsers(addUsersList);
      setLoadedGroupingId(editId);
      setTimeout(() => { isInitialLoad.current = false; }, 500);
    } catch (error) {
      console.error('Error fetching grouping details:', error);
      // Keep the answer: a refused or missing grouping shows the message,
      // never the blank wizard.
      setGroupingLoadProblem(describeLoadFailure(error));
      isInitialLoad.current = false;
    }
  };

  // Resolve linked entities when selections change
  const resolveLinkedEntities = useCallback(async () => {
    if (selectedDealIds.length === 0 && selectedTradeRecapIds.length === 0 && 
        selectedFreightRecapIds.length === 0 && selectedDeliveryIds.length === 0 &&
        selectedBudgetIds.length === 0) {
      setAutoIncludedRaw({ deal_ids: [], trade_recap_ids: [], freight_recap_ids: [], delivery_ids: [], budget_ids: [] });
      return;
    }
    try {
      const response = await axiosInstance.post('/api/operations-access/resolve-linked-entities/', {
        deal_ids: selectedDealIds,
        trade_recap_ids: selectedTradeRecapIds,
        freight_recap_ids: selectedFreightRecapIds,
        delivery_ids: selectedDeliveryIds,
        budget_ids: selectedBudgetIds,
      });
      setAutoIncludedRaw(response.data.auto_included || { deal_ids: [], trade_recap_ids: [], freight_recap_ids: [], delivery_ids: [], budget_ids: [] });
    } catch (error) {
      console.error('Error resolving linked entities:', error);
    }
  }, [selectedDealIds, selectedTradeRecapIds, selectedFreightRecapIds, selectedDeliveryIds, selectedBudgetIds]);

  useEffect(() => {
    const timer = setTimeout(resolveLinkedEntities, 300);
    return () => clearTimeout(timer);
  }, [resolveLinkedEntities]);

  // Bunkers: Office Users span BOTH bunkers desks, independent of which
  // operator is selected — fetch once when in the fuels wizard.
  useEffect(() => {
    if (commodityType === 'fuels') {
      fetchBunkersOfficeUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commodityType]);

  // Solids/Cargo: Office Users follow the selected operator's single office.
  //
  // Resolved through `selectedOperatorDetails`, NOT through `operators.find`.
  // Looking only in the candidate list meant that an operator who had been
  // narrowed out of it resolved to nothing, so the office users of the
  // grouping being edited were never fetched and Step 3 sat empty.
  useEffect(() => {
    if (commodityType !== 'fuels' && selectedOperatorDetails?.office_id) {
      fetchOfficeUsers(selectedOperatorDetails.office_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOperatorDetails, commodityType]);

  // The failure message for both office-user lists. One sentence, one place:
  // whichever list failed, the person needs to know the screen is not claiming
  // the office is empty.
  const officeUsersFailureText = (error) => (
    error.response?.data?.detail
    || error.response?.data?.error
    || 'The list of office users could not be loaded, so nobody is shown here. This is a loading problem, not an empty office. Refresh the page to try again.'
  );

  const fetchOfficeUsers = async (officeId) => {
    setOfficeUsersError(null);
    try {
      const response = await axiosInstance.get(`/api/operations-access/office-users/${officeId}/`);
      setOfficeUsers(response.data);
      // Default all to access + financial access
      const defaults = {};
      response.data.forEach(user => {
        if (!officeUserSettings[user.id]) {
          defaults[user.id] = { has_access: true, has_financial_access: true };
        }
      });
      setOfficeUserSettings(prev => ({ ...defaults, ...prev }));
    } catch (error) {
      console.error('Error fetching office users:', error);
      // A refusal or an outage must never render as "there is nobody in this
      // office" — the admin would go on to save a grouping that silently left
      // the whole office out.
      setOfficeUsers([]);
      setOfficeUsersError(officeUsersFailureText(error));
    }
  };

  // Bunkers Step 3: all active staff across every bunkers office (Asia +
  // Dubai), rather than a single operator's office.
  const fetchBunkersOfficeUsers = async () => {
    setOfficeUsersError(null);
    try {
      const response = await axiosInstance.get('/api/operations-access/bunkers-office-users/');
      setOfficeUsers(response.data);
      const defaults = {};
      response.data.forEach(user => {
        if (!officeUserSettings[user.id]) {
          defaults[user.id] = { has_access: true, has_financial_access: true };
        }
      });
      setOfficeUserSettings(prev => ({ ...defaults, ...prev }));
    } catch (error) {
      console.error('Error fetching bunkers office users:', error);
      setOfficeUsers([]);
      setOfficeUsersError(officeUsersFailureText(error));
    }
  };

  // Fetch all users for step 4
  useEffect(() => {
    if (currentStep === 4) {
      fetchAllUsers();
    }
  }, [currentStep]);

  const fetchAllUsers = async () => {
    setAllUsersError(null);
    try {
      const response = await axiosInstance.get('/api/operations-access/all-users/');
      setAllUsers(response.data);
    } catch (error) {
      console.error('Error fetching all users:', error);
      // Same rule as the two office lists: a failed search must not look like
      // an empty company. Whoever the admin was about to add is still there.
      setAllUsers([]);
      setAllUsersError(
        error.response?.data?.detail
        || error.response?.data?.error
        || 'The list of people could not be loaded, so nobody can be searched here yet. Refresh the page to try again.'
      );
    }
  };

  // Track user interactions for unsaved changes detection
  const markDirty = () => {
    if (!isInitialLoad.current) {
      userHasInteracted.current = true;
    }
  };

  // Cascade unlink: resolve what entity X is linked to, then remove all
  // those linked entities from selections and add them to exclusions
  const cascadeUnlink = async (entityType, entityId) => {
    try {
      const payload = { deal_ids: [], trade_recap_ids: [], freight_recap_ids: [], delivery_ids: [] };
      payload[entityType] = [entityId];

      const response = await axiosInstance.post('/api/operations-access/resolve-linked-entities/', payload);
      const linked = response.data || {};

      // Build sets of IDs to cascade-remove (excluding the entity itself since it's already handled)
      const linkedDealIds = new Set(linked.deal_ids || []);
      const linkedTradeRecapIds = new Set(linked.trade_recap_ids || []);
      const linkedFreightRecapIds = new Set(linked.freight_recap_ids || []);
      const linkedDeliveryIds = new Set(linked.delivery_ids || []);

      // Remove from manual selections
      setSelectedDealIds(prev => prev.filter(id => !linkedDealIds.has(id)));
      setSelectedTradeRecapIds(prev => prev.filter(id => !linkedTradeRecapIds.has(id)));
      setSelectedFreightRecapIds(prev => prev.filter(id => !linkedFreightRecapIds.has(id)));
      setSelectedDeliveryIds(prev => prev.filter(id => !linkedDeliveryIds.has(id)));

      // Add all linked entities to exclusion list so auto-linking doesn't re-add them
      setExcludedAutoIds(prev => ({
        deal_ids: [...new Set([...prev.deal_ids, ...linkedDealIds])],
        trade_recap_ids: [...new Set([...prev.trade_recap_ids, ...linkedTradeRecapIds])],
        freight_recap_ids: [...new Set([...prev.freight_recap_ids, ...linkedFreightRecapIds])],
        delivery_ids: [...new Set([...prev.delivery_ids, ...linkedDeliveryIds])],
      }));
    } catch (error) {
      console.error('Error resolving cascade unlink:', error);
    }
  };

  // Toggle entity selection -- handles both manual and auto-linked items
  const toggleEntitySelection = (id, selectedIds, setSelectedIds, autoKey) => {
    markDirty();
    const isManuallySelected = selectedIds.includes(id);
    const isAutoLinked = autoIncluded[autoKey]?.includes(id);
    const isExcluded = excludedAutoIds[autoKey]?.includes(id);

    if (isManuallySelected || isAutoLinked) {
      // Deselect: remove from manual selections and add to exclusions
      setSelectedIds(prev => prev.filter(x => x !== id));
      setExcludedAutoIds(prev => ({ ...prev, [autoKey]: [...prev[autoKey], id] }));
      // Cascade: also unlink all entities connected to this one
      cascadeUnlink(autoKey, id);
    } else if (isExcluded) {
      // Re-select: manually select and clear exclusion
      setSelectedIds(prev => [...prev, id]);
      setExcludedAutoIds(prev => ({ ...prev, [autoKey]: prev[autoKey].filter(x => x !== id) }));
    } else {
      // Fresh select: add to manual selections and clear any stale exclusion
      setSelectedIds(prev => [...prev, id]);
      setExcludedAutoIds(prev => ({ ...prev, [autoKey]: prev[autoKey].filter(x => x !== id) }));
    }
  };

  const toggleDeal = (id) => toggleEntitySelection(id, selectedDealIds, setSelectedDealIds, 'deal_ids');
  const toggleTradeRecap = (id) => toggleEntitySelection(id, selectedTradeRecapIds, setSelectedTradeRecapIds, 'trade_recap_ids');
  const toggleFreightRecap = (id) => toggleEntitySelection(id, selectedFreightRecapIds, setSelectedFreightRecapIds, 'freight_recap_ids');
  const toggleDelivery = (id) => toggleEntitySelection(id, selectedDeliveryIds, setSelectedDeliveryIds, 'delivery_ids');
  const toggleBudget = (id) => toggleEntitySelection(id, selectedBudgetIds, setSelectedBudgetIds, 'budget_ids');

  // Fuel enquiries: manual-only, no auto-link cascade — plain add/remove toggle.
  const toggleFuelEnquiry = (id) => {
    markDirty();
    setSelectedFuelEnquiryIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Office user toggles
  const toggleOfficeAccess = (userId) => {
    markDirty();
    setOfficeUserSettings(prev => ({
      ...prev,
      [userId]: { ...prev[userId], has_access: !prev[userId]?.has_access }
    }));
  };
  const toggleOfficeFinancial = (userId) => {
    markDirty();
    setOfficeUserSettings(prev => ({
      ...prev,
      [userId]: { ...prev[userId], has_financial_access: !prev[userId]?.has_financial_access }
    }));
  };
  // Bulk select-all / deselect-all for the Office Users columns. The Main
  // Operator row is locked (access is always on; financial follows the
  // operator toggle), so bulk actions skip it and only touch the other users.
  const setAllOfficeAccess = (value) => {
    markDirty();
    setOfficeUserSettings(prev => {
      const next = { ...prev };
      officeUsers.forEach(user => {
        if (user.id === selectedOperatorId) return;
        next[user.id] = {
          ...(next[user.id] || { has_access: true, has_financial_access: true }),
          has_access: value,
        };
      });
      return next;
    });
  };
  const setAllOfficeFinancial = (value) => {
    markDirty();
    setOfficeUserSettings(prev => {
      const next = { ...prev };
      officeUsers.forEach(user => {
        if (user.id === selectedOperatorId) return;
        next[user.id] = {
          ...(next[user.id] || { has_access: true, has_financial_access: true }),
          has_financial_access: value,
        };
      });
      return next;
    });
  };

  // Additional user management
  const addAdditionalUser = (user) => {
    markDirty();
    if (!additionalUsers.find(u => u.id === user.id)) {
      setAdditionalUsers(prev => [...prev, user]);
      setAdditionalUserSettings(prev => ({
        ...prev,
        [user.id]: { has_financial_access: false }
      }));
    }
  };

  const removeAdditionalUser = (userId) => {
    markDirty();
    setAdditionalUsers(prev => prev.filter(u => u.id !== userId));
    setAdditionalUserSettings(prev => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };

  const toggleAdditionalFinancial = (userId) => {
    markDirty();
    setAdditionalUserSettings(prev => ({
      ...prev,
      [userId]: { ...prev[userId], has_financial_access: !prev[userId]?.has_financial_access }
    }));
  };

  // Build members array for submission
  const buildMembers = () => {
    const members = [];

    // Main operator
    if (selectedOperatorId) {
      members.push({
        employee_id: selectedOperatorId,
        has_access: true,
        has_financial_access: operatorFinancialAccess,
        membership_type: 'MAIN_OPERATOR',
      });
    }

    // Office users
    officeUsers.forEach(user => {
      if (user.id === selectedOperatorId) return; // Skip main operator (already added)
      const settings = officeUserSettings[user.id] || { has_access: true, has_financial_access: true };
      members.push({
        employee_id: user.id,
        has_access: settings.has_access,
        has_financial_access: settings.has_financial_access,
        membership_type: 'OFFICE',
      });
    });

    // Additional users
    additionalUsers.forEach(user => {
      if (user.id === selectedOperatorId) return;
      if (officeUsers.find(ou => ou.id === user.id)) return;
      const settings = additionalUserSettings[user.id] || { has_financial_access: false };
      members.push({
        employee_id: user.id,
        has_access: true,
        has_financial_access: settings.has_financial_access,
        membership_type: 'MANUAL',
      });
    });

    return members;
  };

  // Validate all required fields with specific feedback
  const validateGrouping = () => {
    const issues = [];
    if (!groupingName.trim()) {
      issues.push({ step: 0, message: 'Grouping name is required' });
    }
    // Deals are a Solids-only concept; on Fuels they have no card and never count.
    const dealsCount = commodityType === 'fuels' ? 0 : selectedDealIds.length;
    if (dealsCount === 0 && selectedTradeRecapIds.length === 0 &&
        selectedFreightRecapIds.length === 0 && selectedDeliveryIds.length === 0 &&
        selectedBudgetIds.length === 0 && selectedFuelEnquiryIds.length === 0) {
      issues.push({ step: 1, message: 'At least one entity (Deal, Recap, Delivery, Budget, or Fuel Enquiry) must be selected' });
    }
    // The main operator is NOT validated here. It became optional on
    // 2026-09-07: a grouping often exists before anyone is on the desk, and
    // forcing a name made admins park a placeholder person on it — which is
    // not a blank field, it is a wrong one, and it hands that person access to
    // everything in the grouping.
    return issues;
  };

  // Save grouping
  const handleSave = async () => {
    // Row 125: an existing grouping is only ever updated from its own loaded
    // record; the blank wizard would clear its operator and members.
    if (isEditing && loadedGroupingId !== editId) {
      showError("This access grouping hasn't loaded, so it can't be saved. Reload the page and try again.");
      return;
    }

    const issues = validateGrouping();
    if (issues.length > 0) {
      const stepLabels = ['Grouping Name', 'Step 1: Select Entities', 'Step 2: Main Operator', 'Step 3: Office Users', 'Step 4: Additional Users'];
      const messages = issues.map(i => `${stepLabels[i.step]}: ${i.message}`);
      showError(`Please fix the following before saving:\n${messages.join('\n')}`);
      goToStep(issues[0].step === 0 ? 1 : issues[0].step);
      return;
    }

    setSaving(true);
    try {
      // Combine manually selected + auto-included IDs (respecting user exclusions)
      const allDealIds = [...new Set([...selectedDealIds, ...(autoIncluded.deal_ids || [])])];
      const allTradeRecapIds = [...new Set([...selectedTradeRecapIds, ...(autoIncluded.trade_recap_ids || [])])];
      const allFreightRecapIds = [...new Set([...selectedFreightRecapIds, ...(autoIncluded.freight_recap_ids || [])])];
      const allDeliveryIds = [...new Set([...selectedDeliveryIds, ...(autoIncluded.delivery_ids || [])])];
      const allBudgetIds = [...new Set([...selectedBudgetIds, ...(autoIncluded.budget_ids || [])])];

      const payload = {
        name: groupingName.trim(),
        commodity_type: commodityType,
        // Deals are Solids-only; harmless empty array for Fuels groupings.
        deal_ids: commodityType === 'fuels' ? [] : allDealIds,
        trade_recap_ids: allTradeRecapIds,
        freight_recap_ids: allFreightRecapIds,
        delivery_ids: allDeliveryIds,
        budget_ids: allBudgetIds,
        // Fuels-only; harmless empty array for Solids groupings.
        fuel_enquiry_ids: commodityType === 'fuels' ? selectedFuelEnquiryIds : [],
        // Always sent, and deliberately null when nobody is on the desk: on an
        // EDIT this null is what actually removes an operator. Omitting the key
        // instead would leave the old operator in place forever.
        main_operator_id: selectedOperatorId || null,
        main_operator_has_financial_access: operatorFinancialAccess,
        members: buildMembers(),
      };

      if (isEditing) {
        await axiosInstance.put(`/api/operations-access/groupings/${editId}/`, payload);
        showSuccess('Access grouping updated successfully.');
      } else {
        await axiosInstance.post('/api/operations-access/groupings/', payload);
        showSuccess('Access grouping created successfully.');
      }

      invalidateGroupingsCache(commodityType);
      goToTradeOps(history, tradeOpsDesk, { activeTab: 'operations-access' });
    } catch (error) {
      console.error('Error saving access grouping:', error);
      const msg = error.response?.data?.error || 'Failed to save access grouping.';
      showError(msg);
    } finally {
      setSaving(false);
    }
  };

  // Filter function for entity search
  const filterEntity = (entity, fields) => {
    if (!entitySearch.trim()) return true;
    const search = entitySearch.toLowerCase();
    return fields.some(f => String(entity[f] || '').toLowerCase().includes(search));
  };

  // Get IDs that are already selected or auto-included
  const isAutoIncludedDeal = (id) => autoIncluded.deal_ids?.includes(id);
  const isAutoIncludedTradeRecap = (id) => autoIncluded.trade_recap_ids?.includes(id);
  const isAutoIncludedFreightRecap = (id) => autoIncluded.freight_recap_ids?.includes(id);
  const isAutoIncludedDelivery = (id) => autoIncluded.delivery_ids?.includes(id);
  const isAutoIncludedBudget = (id) => autoIncluded.budget_ids?.includes(id);

  // Excluded user IDs for Step 4 (already in office list or is operator)
  const excludedUserIds = new Set([
    selectedOperatorId,
    ...officeUsers.map(u => u.id),
    ...additionalUsers.map(u => u.id),
  ].filter(Boolean));

  const filteredAllUsers = allUsers.filter(u => {
    if (excludedUserIds.has(u.id)) return false;
    if (!userSearch.trim()) return true;
    const s = userSearch.toLowerCase();
    return (u.first_name + ' ' + u.last_name).toLowerCase().includes(s) || u.email.toLowerCase().includes(s);
  });

  const getInitials = (firstName, lastName) => {
    return ((firstName?.[0] || '') + (lastName?.[0] || '')).toUpperCase();
  };

  // Deals are Solids-only; exclude them from the count on Fuels groupings.
  const totalSelectedEntities = (commodityType === 'fuels' ? 0 : selectedDealIds.length) + selectedTradeRecapIds.length + selectedFreightRecapIds.length + selectedDeliveryIds.length + selectedBudgetIds.length + selectedFuelEnquiryIds.length;
  const totalAutoIncluded = (autoIncluded.deal_ids?.length || 0) + (autoIncluded.trade_recap_ids?.length || 0) + (autoIncluded.freight_recap_ids?.length || 0) + (autoIncluded.delivery_ids?.length || 0) + (autoIncluded.budget_ids?.length || 0);

  const deselectAll = () => {
    markDirty();
    setSelectedDealIds([]);
    setSelectedTradeRecapIds([]);
    setSelectedFreightRecapIds([]);
    setSelectedDeliveryIds([]);
    setSelectedBudgetIds([]);
    setSelectedFuelEnquiryIds([]);
    setExcludedAutoIds({ deal_ids: [], trade_recap_ids: [], freight_recap_ids: [], delivery_ids: [], budget_ids: [] });
    setAutoIncludedRaw({ deal_ids: [], trade_recap_ids: [], freight_recap_ids: [], delivery_ids: [], budget_ids: [] });
  };

  // Office Users (Step 3) bulk-toggle state. Excludes the locked Main Operator
  // so "all on" reflects only the users whose toggles can actually change.
  const editableOfficeUsers = officeUsers.filter(u => u.id !== selectedOperatorId);
  const allOfficeAccessOn = editableOfficeUsers.length > 0
    && editableOfficeUsers.every(u => (officeUserSettings[u.id]?.has_access ?? true));
  const allOfficeFinancialOn = editableOfficeUsers.length > 0
    && editableOfficeUsers.every(u => (officeUserSettings[u.id]?.has_financial_access ?? true));

  // Back navigation with unsaved changes protection
  const navigateBack = () => {
    goToTradeOps(history, tradeOpsDesk, { activeTab: 'operations-access' });
  };

  const handleBackClick = () => {
    if (userHasInteracted.current) {
      setShowBackConfirmation(true);
    } else {
      navigateBack();
    }
  };

  const handleConfirmBack = () => {
    userHasInteracted.current = false;
    setShowBackConfirmation(false);
    navigateBack();
  };

  const handleCancelBack = () => {
    setShowBackConfirmation(false);
  };

  const stepNames = ['Select Entities', 'Main Operator', 'Office Users', 'Additional Users'];

  const goToStep = (step) => {
    setCurrentStep(step);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 50);
  };

  if (groupingLoadProblem) {
    return (
      <Layout>
        <div className="ops-access-screen">
          <RecordLoadProblem
            problem={groupingLoadProblem}
            recordName="access grouping"
            areaName="operations access"
            backLabel="Back to Operations Access"
            onBack={handleConfirmBack}
            onRetry={() => setGroupingLoadAttempt((n) => n + 1)}
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="ops-access-screen">
        {/* IMT Screen Header */}
        <div className="hms-screen-header">
          <button 
            className="ops-access-back-button"
            onClick={handleBackClick}
            title="Back to Trade Operations"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>

          <div className="header-logo">
            <img src={logo} alt="IMT Bergbau AG" className="header-logo-img" />
            <div className="ops-access-header-title-content">
              <div className="ops-access-header-stacked-fields">
                <div className="ops-access-header-name-row">
                  <span className="ops-access-header-name-display">
                    {groupingName || (isEditing ? 'Untitled Grouping' : 'New Access Grouping')}
                  </span>
                </div>
                <div className="ops-access-header-info-row">
                  <span className="ops-access-header-type-display">
                    <FontAwesomeIcon icon={faUserShield} /> Operations Access
                  </span>
                  <span className={`ops-access-header-mode-badge ${isEditing ? 'editing' : 'creating'}`}>
                    {isEditing ? 'Editing' : 'Creating'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {isEditing && (
            <button 
              className="ops-access-header-save-btn"
              onClick={handleSave}
              disabled={saving}
            >
              <FontAwesomeIcon icon={faSave} />
              <span>{saving ? 'Saving...' : 'Save Changes'}</span>
            </button>
          )}
        </div>

        <div className="ops-access-form-container">

        {/* Grouping Name */}
        <div className="ops-access-name-card">
          <div className="ops-access-name-card-header">
            <FontAwesomeIcon icon={faTag} className="ops-access-name-card-icon" />
            <label className="ops-access-name-label">Grouping Name</label>
          </div>
          <div className="ops-access-name-input-row">
            <input
              type="text"
              className="ops-access-name-input"
              placeholder="Enter a name for this access grouping..."
              value={groupingName}
              onChange={(e) => { markDirty(); setGroupingName(e.target.value); }}
            />
          </div>
          {!groupingName.trim() && (
            <span className="ops-access-name-hint">This name will be displayed in the groupings list</span>
          )}
        </div>

        {/* Step Progress */}
        <div className="ops-access-progress">
          {stepNames.map((name, idx) => (
            <React.Fragment key={idx}>
              <div 
                className={`ops-access-progress-step ${currentStep > idx + 1 ? 'completed' : ''} ${currentStep === idx + 1 ? 'active' : ''}`}
                onClick={() => goToStep(idx + 1)}
              >
                <div className="ops-access-progress-number">
                  {currentStep > idx + 1 ? <FontAwesomeIcon icon={faCheck} /> : idx + 1}
                </div>
                <span className="ops-access-progress-label">{name}</span>
              </div>
              {idx < stepNames.length - 1 && (
                <div className={`ops-access-progress-connector ${currentStep > idx + 1 ? 'completed' : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Select Entities */}
        {currentStep === 1 && (
          <div className="ops-access-step step-entities">
            <h2 className="ops-access-step-title">
              <FontAwesomeIcon icon={faUserShield} className="ops-access-step-icon" />
              Select Operations Entities
            </h2>
            <p className="ops-access-step-desc">
              Select the Deals, Recaps, Deliveries, and Budgets to include in this access grouping. Linked entities will be auto-included.
            </p>

            {/* Selection Summary */}
            <div className="ops-access-selection-summary">
              {commodityType === 'fuels' && (
                <span className="ops-access-summary-pill fuel-enquiries">
                  <FontAwesomeIcon icon={faTint} /> {selectedFuelEnquiryIds.length} Fuel Enquiries
                </span>
              )}
              {commodityType !== 'fuels' && (
                <span className="ops-access-summary-pill deals">
                  <FontAwesomeIcon icon={faHandshake} /> {selectedDealIds.length + (autoIncluded.deal_ids?.filter(id => !selectedDealIds.includes(id)).length || 0)} Deals
                </span>
              )}
              <span className="ops-access-summary-pill recaps">
                <FontAwesomeIcon icon={faFileContract} /> {selectedTradeRecapIds.length + selectedFreightRecapIds.length + (autoIncluded.trade_recap_ids?.filter(id => !selectedTradeRecapIds.includes(id)).length || 0) + (autoIncluded.freight_recap_ids?.filter(id => !selectedFreightRecapIds.includes(id)).length || 0)} Recaps
              </span>
              <span className="ops-access-summary-pill deliveries">
                <FontAwesomeIcon icon={faTruck} /> {selectedDeliveryIds.length + (autoIncluded.delivery_ids?.filter(id => !selectedDeliveryIds.includes(id)).length || 0)} Deliveries
              </span>
              <span className="ops-access-summary-pill budgets">
                <FontAwesomeIcon icon={faCalculator} /> {selectedBudgetIds.length + (autoIncluded.budget_ids?.filter(id => !selectedBudgetIds.includes(id)).length || 0)} Budgets
              </span>
              {totalAutoIncluded > 0 && (
                <span className="ops-access-summary-pill auto">
                  <FontAwesomeIcon icon={faCheck} /> {totalAutoIncluded} Auto-linked
                </span>
              )}
              {(totalSelectedEntities > 0 || totalAutoIncluded > 0) && (
                <button className="ops-access-deselect-all-btn" onClick={deselectAll}>
                  <FontAwesomeIcon icon={faTimes} /> Deselect All
                </button>
              )}
            </div>

            <div className="ops-access-search-bar">
              <FontAwesomeIcon icon={faSearch} className="ops-access-search-icon" />
              <input
                type="text"
                placeholder="Filter by deal name, transaction number, recap type, or delivery number..."
                value={entitySearch}
                onChange={(e) => setEntitySearch(e.target.value)}
              />
            </div>

            {loadingEntities ? (
              <div className="ops-access-loading-text">Loading entities...</div>
            ) : (
              <div className="ops-access-entity-sections">
                {/* Fuel Enquiries (Fuels groupings only — manual selection) */}
                {commodityType === 'fuels' && (
                  <div className="ops-access-entity-section">
                    <div className="ops-access-entity-section-header fuel-enquiries">
                      <div className="ops-access-entity-section-title">
                        <FontAwesomeIcon icon={faTint} />
                        <span>Fuel Enquiries</span>
                      </div>
                      <span className="ops-access-entity-section-count">{selectedFuelEnquiryIds.length} / {availableEntities.fuel_enquiries?.length || 0}</span>
                    </div>
                    <div className="ops-access-entity-list">
                      {(availableEntities.fuel_enquiries || [])
                        .filter(fe => filterEntity(fe, ['display_id', 'vessel_name']))
                        .map(fe => {
                          const selected = selectedFuelEnquiryIds.includes(fe.id);
                          return (
                            <div
                              key={fe.id}
                              className={`ops-access-entity-item ${selected ? 'selected' : ''} accent-fuel-enquiries`}
                              onClick={() => toggleFuelEnquiry(fe.id)}
                            >
                              <FontAwesomeIcon
                                icon={selected ? faCheckSquare : faSquare}
                                className={`ops-access-checkbox ${selected ? 'checked' : ''}`}
                              />
                              <div className="ops-access-entity-info">
                                <span className="ops-access-entity-name">{fe.display_id}{fe.vessel_name ? ` - ${fe.vessel_name}` : ''}</span>
                                <span className="ops-access-entity-meta">{fe.stage}</span>
                              </div>
                            </div>
                          );
                      })}
                      {(availableEntities.fuel_enquiries || []).length === 0 && <div className="ops-access-entity-empty">No available fuel enquiries</div>}
                    </div>
                  </div>
                )}

                {/* Deals (Solids only — Fuels/bunker has no Deals concept) */}
                {commodityType !== 'fuels' && (
                  <div className="ops-access-entity-section">
                    <div className="ops-access-entity-section-header deals">
                      <div className="ops-access-entity-section-title">
                        <FontAwesomeIcon icon={faHandshake} />
                        <span>Deals</span>
                      </div>
                      <span className="ops-access-entity-section-count">{selectedDealIds.length + (autoIncluded.deal_ids?.filter(id => !selectedDealIds.includes(id)).length || 0)} / {availableEntities.deals?.length || 0}</span>
                    </div>
                    <div className="ops-access-entity-list">
                      {(availableEntities.deals || [])
                        .filter(d => filterEntity(d, ['display_name', 'transaction_number']))
                        .map(deal => {
                          const selected = selectedDealIds.includes(deal.offer_id);
                          const autoInc = isAutoIncludedDeal(deal.offer_id);
                          return (
                            <div
                              key={deal.offer_id}
                              className={`ops-access-entity-item ${selected ? 'selected' : ''} ${autoInc ? 'auto-included' : ''} accent-deals`}
                              onClick={() => toggleDeal(deal.offer_id)}
                            >
                              <FontAwesomeIcon
                                icon={selected || autoInc ? faCheckSquare : faSquare}
                                className={`ops-access-checkbox ${selected ? 'checked' : ''} ${autoInc ? 'auto' : ''}`}
                              />
                              <div className="ops-access-entity-info">
                                <span className="ops-access-entity-name">{deal.display_name || deal.transaction_number}</span>
                                <span className="ops-access-entity-meta">{deal.transaction_number} - {deal.offer_transaction_type}</span>
                              </div>
                              {autoInc && <span className="ops-access-auto-badge">Auto-linked</span>}
                            </div>
                          );
                      })}
                      {(availableEntities.deals || []).length === 0 && <div className="ops-access-entity-empty">No available deals</div>}
                    </div>
                  </div>
                )}

                {/* Trade Recaps */}
                <div className="ops-access-entity-section">
                  <div className="ops-access-entity-section-header recaps">
                    <div className="ops-access-entity-section-title">
                      <FontAwesomeIcon icon={faFileContract} />
                      <span>Trade Recaps</span>
                    </div>
                    <span className="ops-access-entity-section-count">{selectedTradeRecapIds.length + (autoIncluded.trade_recap_ids?.filter(id => !selectedTradeRecapIds.includes(id)).length || 0)} / {availableEntities.trade_recaps?.length || 0}</span>
                  </div>
                  <div className="ops-access-entity-list">
                    {(availableEntities.trade_recaps || [])
                      .filter(r => filterEntity(r, ['display_name', 'transaction_number']))
                      .map(recap => {
                        const selected = selectedTradeRecapIds.includes(recap.id);
                        const autoInc = isAutoIncludedTradeRecap(recap.id);
                        return (
                          <div 
                            key={recap.id}
                            className={`ops-access-entity-item ${selected ? 'selected' : ''} ${autoInc ? 'auto-included' : ''} accent-recaps`}
                            onClick={() => toggleTradeRecap(recap.id)}
                          >
                            <FontAwesomeIcon 
                              icon={selected || autoInc ? faCheckSquare : faSquare} 
                              className={`ops-access-checkbox ${selected ? 'checked' : ''} ${autoInc ? 'auto' : ''}`}
                            />
                            <div className="ops-access-entity-info">
                              <span className="ops-access-entity-name">{recap.display_name || recap.transaction_number}</span>
                              <span className="ops-access-entity-meta">{recap.transaction_number} - {recap.recap_type}</span>
                            </div>
                            {autoInc && <span className="ops-access-auto-badge">Auto-linked</span>}
                          </div>
                        );
                    })}
                    {(availableEntities.trade_recaps || []).length === 0 && <div className="ops-access-entity-empty">No available trade recaps</div>}
                  </div>
                </div>

                {/* Freight Recaps */}
                <div className="ops-access-entity-section">
                  <div className="ops-access-entity-section-header freight">
                    <div className="ops-access-entity-section-title">
                      <FontAwesomeIcon icon={faFileContract} />
                      <span>Freight Recaps</span>
                    </div>
                    <span className="ops-access-entity-section-count">{selectedFreightRecapIds.length + (autoIncluded.freight_recap_ids?.filter(id => !selectedFreightRecapIds.includes(id)).length || 0)} / {availableEntities.freight_recaps?.length || 0}</span>
                  </div>
                  <div className="ops-access-entity-list">
                    {(availableEntities.freight_recaps || [])
                      .filter(f => filterEntity(f, ['recap_display_name', 'transaction_number']))
                      .map(freight => {
                        const selected = selectedFreightRecapIds.includes(freight.id);
                        const autoInc = isAutoIncludedFreightRecap(freight.id);
                        return (
                          <div 
                            key={freight.id}
                            className={`ops-access-entity-item ${selected ? 'selected' : ''} ${autoInc ? 'auto-included' : ''} accent-freight`}
                            onClick={() => toggleFreightRecap(freight.id)}
                          >
                            <FontAwesomeIcon 
                              icon={selected || autoInc ? faCheckSquare : faSquare} 
                              className={`ops-access-checkbox ${selected ? 'checked' : ''} ${autoInc ? 'auto' : ''}`}
                            />
                            <div className="ops-access-entity-info">
                              <span className="ops-access-entity-name">{freight.recap_display_name || freight.transaction_number}</span>
                              <span className="ops-access-entity-meta">{freight.transaction_number}</span>
                            </div>
                            {autoInc && <span className="ops-access-auto-badge">Auto-linked</span>}
                          </div>
                        );
                    })}
                    {(availableEntities.freight_recaps || []).length === 0 && <div className="ops-access-entity-empty">No available freight recaps</div>}
                  </div>
                </div>

                {/* Deliveries */}
                <div className="ops-access-entity-section">
                  <div className="ops-access-entity-section-header deliveries">
                    <div className="ops-access-entity-section-title">
                      <FontAwesomeIcon icon={faTruck} />
                      <span>Deliveries</span>
                    </div>
                    <span className="ops-access-entity-section-count">{selectedDeliveryIds.length + (autoIncluded.delivery_ids?.filter(id => !selectedDeliveryIds.includes(id)).length || 0)} / {availableEntities.deliveries?.length || 0}</span>
                  </div>
                  <div className="ops-access-entity-list">
                    {(availableEntities.deliveries || [])
                      .filter(d => filterEntity(d, ['display_name', 'delivery_unique_number']))
                      .map(delivery => {
                        const selected = selectedDeliveryIds.includes(delivery.id);
                        const autoInc = isAutoIncludedDelivery(delivery.id);
                        return (
                          <div 
                            key={delivery.id}
                            className={`ops-access-entity-item ${selected ? 'selected' : ''} ${autoInc ? 'auto-included' : ''} accent-deliveries`}
                            onClick={() => toggleDelivery(delivery.id)}
                          >
                            <FontAwesomeIcon 
                              icon={selected || autoInc ? faCheckSquare : faSquare} 
                              className={`ops-access-checkbox ${selected ? 'checked' : ''} ${autoInc ? 'auto' : ''}`}
                            />
                            <div className="ops-access-entity-info">
                              <span className="ops-access-entity-name">{delivery.display_name || delivery.delivery_unique_number}</span>
                              <span className="ops-access-entity-meta">{delivery.delivery_unique_number}</span>
                            </div>
                            {autoInc && <span className="ops-access-auto-badge">Auto-linked</span>}
                          </div>
                        );
                    })}
                    {(availableEntities.deliveries || []).length === 0 && <div className="ops-access-entity-empty">No available deliveries</div>}
                  </div>
                </div>

                {/* Budgets */}
                <div className="ops-access-entity-section">
                  <div className="ops-access-entity-section-header budgets">
                    <div className="ops-access-entity-section-title">
                      <FontAwesomeIcon icon={faCalculator} />
                      <span>Budgets</span>
                    </div>
                    <span className="ops-access-entity-section-count">{selectedBudgetIds.length + (autoIncluded.budget_ids?.filter(id => !selectedBudgetIds.includes(id)).length || 0)} / {availableEntities.budgets?.length || 0}</span>
                  </div>
                  <div className="ops-access-entity-list">
                    {(availableEntities.budgets || [])
                      .filter(b => filterEntity(b, ['display_name', 'currency_code', 'offer_group_identifier']))
                      .map(budget => {
                        const selected = selectedBudgetIds.includes(budget.id);
                        const autoInc = isAutoIncludedBudget(budget.id);
                        return (
                          <div 
                            key={budget.id}
                            className={`ops-access-entity-item ${selected ? 'selected' : ''} ${autoInc ? 'auto-included' : ''} accent-budgets`}
                            onClick={() => toggleBudget(budget.id)}
                          >
                            <FontAwesomeIcon 
                              icon={selected || autoInc ? faCheckSquare : faSquare} 
                              className={`ops-access-checkbox ${selected ? 'checked' : ''} ${autoInc ? 'auto' : ''}`}
                            />
                            <div className="ops-access-entity-info">
                              <span className="ops-access-entity-name">{budget.display_name || 'Untitled'}</span>
                              <span className="ops-access-entity-meta">{budget.currency_code} - {budget.status}{budget.offer_group_identifier ? ` - ${budget.offer_group_identifier}` : ''}</span>
                            </div>
                            {autoInc && <span className="ops-access-auto-badge">Auto-linked</span>}
                          </div>
                        );
                    })}
                    {(availableEntities.budgets || []).length === 0 && <div className="ops-access-entity-empty">No available budgets</div>}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Main Operator */}
        {currentStep === 2 && (
          <div className="ops-access-step step-operator">
            <h2 className="ops-access-step-title">
              <FontAwesomeIcon icon={faUser} className="ops-access-step-icon" />
              Select Main Operator
            </h2>
            <p className="ops-access-step-desc">
              Choose the primary operator for this access grouping. Their office colleagues will be auto-included in the next step.
              {' '}<strong>Optional</strong> — leave it unset if nobody is on the desk yet; everyone you add in the next steps still gets their access.
            </p>

            <div className="ops-access-search-bar">
              <FontAwesomeIcon icon={faSearch} className="ops-access-search-icon" />
              <input
                type="text"
                placeholder="Search by operator name or email address..."
                value={operatorSearch}
                onChange={(e) => setOperatorSearch(e.target.value)}
              />
            </div>

            {/* AC8 — an empty dropdown must SAY SO, and say which of the three
                reasons it is. An empty box with no words is what gets reported
                as "the operator list is broken". */}
            {loadingOperators ? (
              <div className="ops-access-empty-state-mini" data-testid="operators-loading">
                <FontAwesomeIcon icon={faUser} className="ops-access-empty-state-icon" />
                <span>Loading operators…</span>
              </div>
            ) : operatorsError ? (
              <div className="ops-access-empty-state-mini" data-testid="operators-error">
                <FontAwesomeIcon icon={faUser} className="ops-access-empty-state-icon" />
                <span>{operatorsError}</span>
              </div>
            ) : operatorsToShow.length === 0 ? (
              <div className="ops-access-empty-state-mini" data-testid="operators-empty">
                <FontAwesomeIcon icon={faUser} className="ops-access-empty-state-icon" />
                <span>
                  {commodityType === 'fuels'
                    ? 'No operators to choose from. The Bunkers list shows people whose department is marked as a Fuels operations desk — ask an administrator to mark the department in HR Settings. You can still save this grouping without a main operator.'
                    : 'No operators to choose from. The Solids list shows people whose job title is an Operator role and is still switched on — ask an administrator to set the job title in HR Settings. You can still save this grouping without a main operator.'}
                </span>
              </div>
            ) : null}

            {/* The list came back WIDER than the rule intends, and the server
                said why. Printing its sentence rather than composing our own
                keeps the explanation and the rule in one place. */}
            {!loadingOperators && !operatorsError && operatorsNotice ? (
              <div
                className="ops-access-empty-state-mini"
                data-testid="operators-notice"
                style={{ color: '#8a6d3b', background: '#fcf8e3', border: '1px solid #faebcc' }}
              >
                <FontAwesomeIcon icon={faUser} className="ops-access-empty-state-icon" />
                <span>{operatorsNotice}</span>
              </div>
            ) : null}

            <div className="ops-access-operator-list">
              {operatorsToShow
                .filter(op => {
                  if (!operatorSearch.trim()) return true;
                  const s = operatorSearch.toLowerCase();
                  return (op.first_name + ' ' + op.last_name).toLowerCase().includes(s) || op.email.toLowerCase().includes(s);
                })
                .map(op => (
                  <div 
                    key={op.id}
                    className={`ops-access-operator-item ${selectedOperatorId === op.id ? 'selected' : ''}`}
                    onClick={() => { markDirty(); setSelectedOperatorId(op.id); }}
                  >
                    <div className="ops-access-avatar">
                      {getInitials(op.first_name, op.last_name)}
                    </div>
                    <div className="ops-access-operator-info">
                      <span className="ops-access-operator-name">{op.first_name} {op.last_name}</span>
                      <span className="ops-access-operator-meta">{op.position_name}</span>
                      {op.office_name && <span className="ops-access-operator-office"><FontAwesomeIcon icon={faBuilding} /> {op.office_name}</span>}
                    </div>
                    <div className="ops-access-operator-radio">
                      <div className={`ops-access-radio ${selectedOperatorId === op.id ? 'active' : ''}`} />
                    </div>
                  </div>
                ))}
            </div>

            {selectedOperatorId && (() => {
              // The operator this grouping HAS — not "the operator, if they
              // happen to still be an offered candidate". See
              // `selectedOperatorDetails` above.
              const selOp = selectedOperatorDetails;
              return (
                <div className="ops-access-operator-selected-card">
                  {/* AC6: NAME the person. This card used to show a financial
                      access toggle and an office callout for somebody it never
                      named, so an operator who had dropped out of the narrowed
                      list left the admin looking at settings for a blank. */}
                  <div
                    className="ops-access-operator-selected-name"
                    data-testid="selected-operator-name"
                    style={{ fontWeight: 600, color: '#1f2d3d', marginBottom: '6px' }}
                  >
                    {selOp
                      ? `${selOp.first_name} ${selOp.last_name}`
                      : 'This grouping has a main operator, but their details could not be loaded. Refresh the page before saving — saving now could remove them.'}
                    {selOp && selOp.is_active === false && (
                      <span
                        data-testid="selected-operator-inactive"
                        style={{ marginLeft: '8px', fontWeight: 400, color: '#c0392b' }}
                      >
                        — no longer an active employee
                      </span>
                    )}
                  </div>
                  {selectedOperatorIsNoLongerACandidate && (
                    <div
                      data-testid="selected-operator-not-a-candidate"
                      style={{ marginBottom: '10px', fontSize: '13px', color: '#8a6d3b' }}
                    >
                      This person stays the main operator of this grouping, but
                      they would not appear in the list for a new grouping any
                      more — their job title or operations desk has changed.
                    </div>
                  )}
                  {/* Without this there is no way BACK to "nobody". The picker
                      is a set of radio rows, and a radio cannot be unpicked —
                      so making the field optional in the database alone would
                      still have left an existing operator impossible to remove
                      once the person had left the desk.

                      Styled inline on purpose: the stylesheet has no rule for
                      this new class, and an unstyled control that inherits a
                      near-background colour reads as "empty/broken" rather than
                      as a button. Explicit colours, never inherited ones. */}
                  <button
                    type="button"
                    className="ops-access-clear-operator-btn"
                    onClick={() => { markDirty(); setSelectedOperatorId(null); setOperatorFinancialAccess(false); }}
                    style={{
                      alignSelf: 'flex-start',
                      marginBottom: '10px',
                      padding: '6px 12px',
                      border: '1px solid #c0392b',
                      borderRadius: '4px',
                      background: '#fff',
                      color: '#c0392b',
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    Clear main operator
                  </button>
                  {commodityType === 'fuels' ? (
                    <div className="ops-access-office-callout">
                      <FontAwesomeIcon icon={faBuilding} className="ops-access-office-callout-icon" />
                      <span>All <strong>Bunkers</strong> office users (Asia &amp; Dubai) will be added in the next step</span>
                    </div>
                  ) : selOp?.office_name && (
                    <div className="ops-access-office-callout">
                      <FontAwesomeIcon icon={faBuilding} className="ops-access-office-callout-icon" />
                      <span>Office users from <strong>{selOp.office_name}</strong> will be added in the next step</span>
                    </div>
                  )}
                  <div className="ops-access-financial-card">
                    <div className="ops-access-financial-card-left">
                      <FontAwesomeIcon icon={faMoneyBillWave} className="ops-access-financial-card-icon" />
                      <div className="ops-access-financial-card-text">
                        <span className="ops-access-financial-card-title">Financial Access</span>
                        <span className="ops-access-financial-card-desc">Allow this operator to view AR/AP items</span>
                      </div>
                    </div>
                    <label className="ops-access-toggle-switch" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={operatorFinancialAccess} onChange={() => { markDirty(); setOperatorFinancialAccess(!operatorFinancialAccess); }} />
                      <span className="ops-access-toggle-slider"></span>
                    </label>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Step 3: Office Users */}
        {currentStep === 3 && (
          <div className="ops-access-step step-office">
            <h2 className="ops-access-step-title">
              <FontAwesomeIcon icon={faBuilding} className="ops-access-step-icon" />
              Office Users
            </h2>
            <p className="ops-access-step-desc">
              {commodityType === 'fuels'
                ? 'All users across the Bunkers offices (Asia & Dubai) are auto-included with full access. Toggle access and financial visibility for each user.'
                : "All users in the main operator's office are auto-included with full access. Toggle access and financial visibility for each user."}
            </p>

            {officeUsersError ? (
              /* A refusal or an outage is NOT "this office is empty". Saying
                 nothing here would let an admin save a grouping that quietly
                 left out everybody the failed request would have listed. */
              <div className="ops-access-empty-state-mini" data-testid="office-users-error">
                <FontAwesomeIcon icon={faBuilding} className="ops-access-empty-state-icon" />
                <span>{officeUsersError}</span>
              </div>
            ) : officeUsers.length === 0 ? (
              <div className="ops-access-empty-state-mini" data-testid="office-users-empty">
                <FontAwesomeIcon icon={faBuilding} className="ops-access-empty-state-icon" />
                <span>
                  {selectedOperatorId
                    ? 'No users found in this office.'
                    : commodityType === 'fuels'
                      ? 'No users found in the Bunkers offices.'
                      // Solids pulls this list from the main operator's own
                      // office, so with no operator there is nothing to pull.
                      // Says what to do, and that it is not compulsory.
                      : 'This list follows the main operator’s office, and no main operator is set. Pick one in the previous step, or skip it and add people directly in the next step.'}
                </span>
              </div>
            ) : (
              <div className="ops-access-office-list">
                <div className="ops-access-office-header">
                  <span className="ops-access-office-col-name">Team Member</span>
                  <span className="ops-access-office-col-toggle ops-access-office-col-toggle-head">
                    Access
                    {editableOfficeUsers.length > 0 && (
                      <label
                        className="ops-access-toggle-switch header-master"
                        title={allOfficeAccessOn ? 'Deselect all' : 'Select all'}
                      >
                        <input
                          type="checkbox"
                          checked={allOfficeAccessOn}
                          onChange={() => setAllOfficeAccess(!allOfficeAccessOn)}
                        />
                        <span className="ops-access-toggle-slider"></span>
                      </label>
                    )}
                  </span>
                  <span className="ops-access-office-col-toggle ops-access-office-col-toggle-head">
                    Financial
                    {editableOfficeUsers.length > 0 && (
                      <label
                        className="ops-access-toggle-switch header-master"
                        title={allOfficeFinancialOn ? 'Deselect all' : 'Select all'}
                      >
                        <input
                          type="checkbox"
                          checked={allOfficeFinancialOn}
                          onChange={() => setAllOfficeFinancial(!allOfficeFinancialOn)}
                        />
                        <span className="ops-access-toggle-slider"></span>
                      </label>
                    )}
                  </span>
                </div>
                {officeUsers.map(user => {
                  const isOperator = user.id === selectedOperatorId;
                  const settings = officeUserSettings[user.id] || { has_access: true, has_financial_access: true };
                  return (
                    <div key={user.id} className={`ops-access-office-row ${isOperator ? 'is-operator' : ''}`}>
                      <div className="ops-access-office-col-name">
                        <div className="ops-access-office-user-row">
                          <div className={`ops-access-avatar small ${isOperator ? 'operator' : ''}`}>
                            {getInitials(user.first_name, user.last_name)}
                          </div>
                          <div className="ops-access-office-user-details">
                            <span className="ops-access-office-user-name">{user.first_name} {user.last_name}</span>
                            <span className="ops-access-office-user-meta">{user.position_name || user.email}</span>
                          </div>
                        </div>
                        {isOperator && <span className="ops-access-operator-badge">Main Operator</span>}
                      </div>
                      <div className="ops-access-office-col-toggle">
                        {isOperator ? (
                          <label className="ops-access-toggle-switch disabled">
                            <input type="checkbox" checked={true} readOnly disabled />
                            <span className="ops-access-toggle-slider"></span>
                          </label>
                        ) : (
                          <label className="ops-access-toggle-switch">
                            <input type="checkbox" checked={settings.has_access} onChange={() => toggleOfficeAccess(user.id)} />
                            <span className="ops-access-toggle-slider"></span>
                          </label>
                        )}
                      </div>
                      <div className="ops-access-office-col-toggle">
                        {isOperator ? (
                          <label className="ops-access-toggle-switch disabled">
                            <input type="checkbox" checked={operatorFinancialAccess} readOnly disabled />
                            <span className="ops-access-toggle-slider"></span>
                          </label>
                        ) : (
                          <label className="ops-access-toggle-switch">
                            <input type="checkbox" checked={settings.has_financial_access} onChange={() => toggleOfficeFinancial(user.id)} />
                            <span className="ops-access-toggle-slider"></span>
                          </label>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Step 4: Additional Users */}
        {currentStep === 4 && (
          <div className="ops-access-step step-additional">
            <h2 className="ops-access-step-title">
              <FontAwesomeIcon icon={faUsers} className="ops-access-step-icon" />
              Additional Users
            </h2>
            <p className="ops-access-step-desc">
              Add any other users from across the system who should have access to this grouping.
            </p>

            <div className="ops-access-search-bar">
              <FontAwesomeIcon icon={faSearch} className="ops-access-search-icon" />
              <input
                type="text"
                placeholder="Search by name or email to add users from other offices..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
              />
            </div>

            {allUsersError && (
              /* "No matching users found" would be a lie here — the search has
                 nothing to search. Say which it is. */
              <div className="ops-access-empty-state-mini" data-testid="all-users-error">
                <FontAwesomeIcon icon={faUsers} className="ops-access-empty-state-icon" />
                <span>{allUsersError}</span>
              </div>
            )}

            {!allUsersError && userSearch.trim() && (
              <div className="ops-access-user-search-results">
                {filteredAllUsers.slice(0, 10).map(user => (
                  <div 
                    key={user.id} 
                    className="ops-access-user-search-item"
                    onClick={() => { addAdditionalUser(user); setUserSearch(''); }}
                  >
                    <div className="ops-access-user-search-left">
                      <div className="ops-access-avatar small">
                        {getInitials(user.first_name, user.last_name)}
                      </div>
                      <div className="ops-access-user-search-info">
                        <span className="ops-access-user-search-name">{user.first_name} {user.last_name}</span>
                        <span className="ops-access-user-search-meta">{user.office_name || ''} - {user.position_name || user.email}</span>
                      </div>
                    </div>
                    <div className="ops-access-user-search-add">
                      <FontAwesomeIcon icon={faPlus} />
                    </div>
                  </div>
                ))}
                {filteredAllUsers.length === 0 && (
                  <div className="ops-access-user-search-empty">No matching users found</div>
                )}
              </div>
            )}

            {additionalUsers.length > 0 && (
              <div className="ops-access-additional-list">
                <div className="ops-access-additional-list-header">
                  <span>Added Users ({additionalUsers.length})</span>
                </div>
                {additionalUsers.map(user => {
                  const settings = additionalUserSettings[user.id] || { has_financial_access: false };
                  return (
                    <div key={user.id} className="ops-access-additional-row">
                      <div className="ops-access-additional-left">
                        <div className="ops-access-avatar small">
                          {getInitials(user.first_name, user.last_name)}
                        </div>
                        <div className="ops-access-additional-info">
                          <span className="ops-access-additional-name">{user.first_name} {user.last_name}</span>
                          <span className="ops-access-additional-meta">{user.office_name || ''} - {user.position_name || user.email}</span>
                        </div>
                      </div>
                      <div className="ops-access-additional-controls">
                        <div className="ops-access-additional-financial-label">
                          <FontAwesomeIcon icon={faMoneyBillWave} />
                          <span>Financial</span>
                        </div>
                        <label className="ops-access-toggle-switch">
                          <input type="checkbox" checked={settings.has_financial_access} onChange={() => toggleAdditionalFinancial(user.id)} />
                          <span className="ops-access-toggle-slider"></span>
                        </label>
                        <button 
                          className="ops-access-remove-btn"
                          onClick={() => removeAdditionalUser(user.id)}
                          title="Remove user"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {additionalUsers.length === 0 && !userSearch.trim() && (
              <div className="ops-access-empty-state-mini">
                <FontAwesomeIcon icon={faUserPlus} className="ops-access-empty-state-icon" />
                <span>Search for users above to add them to this access grouping.</span>
              </div>
            )}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="ops-access-nav-buttons">
          {currentStep > 1 && (
            <button 
              className="ops-access-nav-btn secondary"
              onClick={() => goToStep(currentStep - 1)}
            >
              <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: '6px' }} />
              Previous
            </button>
          )}
          <div className="ops-access-nav-spacer" />
          {currentStep < 4 ? (
            <button 
              className="ops-access-nav-btn primary"
              onClick={() => {
                if (currentStep === 1) {
                  if (!groupingName.trim()) {
                    showError('Please enter a grouping name before proceeding.');
                    return;
                  }
                  // Deals are Solids-only; on Fuels they never count toward the gate.
                  const dealsCount = commodityType === 'fuels' ? 0 : selectedDealIds.length;
                  if (dealsCount === 0 && selectedTradeRecapIds.length === 0 &&
                      selectedFreightRecapIds.length === 0 && selectedDeliveryIds.length === 0 &&
                      selectedBudgetIds.length === 0 && selectedFuelEnquiryIds.length === 0) {
                    showError('Please select at least one entity (Deal, Recap, Delivery, Budget, or Fuel Enquiry) before proceeding.');
                    return;
                  }
                }
                // No gate on step 2: the main operator is optional, so moving
                // on without one is a legitimate choice, not a mistake.
                goToStep(currentStep + 1);
              }}
            >
              Next
              <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '6px' }} />
            </button>
          ) : (
            <button 
              className="ops-access-nav-btn save"
              onClick={handleSave}
              disabled={saving}
            >
              <FontAwesomeIcon icon={faSave} style={{ marginRight: '6px' }} />
              {saving ? 'Saving...' : (isEditing ? 'Update Grouping' : 'Create Grouping')}
            </button>
          )}
        </div>
        </div>

        {showBackConfirmation && (
          <DeliveryBackModal
            message="You have unsaved changes to this access grouping. Are you sure you want to leave?"
            onConfirm={handleConfirmBack}
            onCancel={handleCancelBack}
          />
        )}

        <BackToTopButton />
        <Footer />
      </div>
    </Layout>
  );
};

export default OperationsAccessForm;
